// The Look portal editor (#103) — author a Look (palette + dither style + b/c/s +
// lum) and see a FAITHFUL preview: the source is reduced to the camera's RGB565 and
// run through the Engine twin (cores/preview.previewLook), so the round canvas shows
// byte-for-byte what the camera would render (ADR-0028). The source is an uploaded
// image or a built-in demo scene; commit + the Reference loop come later (#105/#107).
//
// Testability follows the repo idiom: the editable Look state is fully reflected in
// the form controls + reported via `onLookChange`, and the canvas draw is guarded
// (jsdom has no 2D context), so the interaction is unit-tested without real canvas.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Device } from "../api";
import { previewLook, paletteFromHex } from "../cores/preview";
import { LivePreviewPanel } from "./LivePreviewPanel";
import { ColorPicker } from "./ColorPicker";
import { paletteFromImage } from "../cores/paletteFromImage";
import { STYLE_NAMES, MAX_COLORS_PER_PALETTE, type MapMode } from "../cores/dither";
import { decodeGif, type GifFrame } from "../cores/gifDecode";
import { frameForTime, type FrameTiming } from "../cores/mosaic";
import { BUILTIN_PRESETS, loadUserPresets, saveUserPreset, deleteUserPreset, type PalettePreset } from "./palettePresets";
import { STARTER_LOOKS } from "./starterLooks.generated";

const VIEW = 240; // the device View size (square)

// R27: opaque, monotonic per-swatch ids for React keys. Colours can duplicate and are
// reordered/removed mid-list, so keying by array index (or by hex value) reused the wrong
// DOM node — stranding the native colour-picker popover/focus on the neighbour. A stable id
// that MOVES with its colour (see colorIds below) keeps each <input> bound to its swatch.
let COLOR_ID_SEQ = 0;
function mkColorId(): string {
  return `c${COLOR_ID_SEQ++}`;
}

// Plain-language help for the Dither-style picker, keyed by STYLE_NAMES and verified
// against cores/dither.ts (ordered: Bayer/Ordered2/Halftone; error-diffusion:
// Floyd/Atkinson/Sierra; Block = mosaic; ASCII = glyphs on the darkest "paper").
const DITHER_INTRO =
  "Dithering arranges a few palette colours in patterns so your eye blends them into shades the palette doesn't actually contain — trading colour count for the illusion of more. Ordered styles (Bayer, Ordered2, Halftone) stamp a fixed texture; error-diffusion styles (Floyd, Atkinson, Sierra) scatter each pixel's rounding error for smoother gradients. Hover a style to see what it does.";
const STYLE_HELP: Record<string, string> = {
  Nearest: "No dithering — every pixel snaps to its single closest palette colour. Flat, poster-like areas with hard edges between tones.",
  Bayer: "Ordered dithering through a 4×4 Bayer matrix — a fixed cross-hatch threshold. Clean, uniform retro stipple; fast and predictable, never smudged.",
  Floyd: "Floyd–Steinberg error diffusion — each pixel's rounding error is pushed onto its neighbours. Smooth, organic gradients; the classic newsprint-photo look.",
  Atkinson: "Atkinson error diffusion (classic Macintosh) — spreads only part of the error, so highlights and shadows stay crisp and contrast is punchy (extremes can blow out).",
  Sierra: "Sierra error diffusion — a wider spread than Floyd–Steinberg. Smooth gradients with a softer, finer grain.",
  Ordered2: "Ordered dithering through a tiny 2×2 matrix — a bold, coarse checkerboard. Chunky, high-texture, very retro.",
  Halftone: "Clustered-dot ordered dithering — pixels gather into dots that grow with darkness, like printed newsprint or comic-book shading.",
  Block: "Mosaic quantise — averages each small block of pixels to one palette colour. A chunky, low-resolution pixel-block look.",
  ASCII: "Renders tones as coloured text glyphs (sparse → dense) over the palette's darkest colour as “paper”. A terminal / text-art effect.",
  BlueNoise: "Ordered dithering through a 64×64 blue-noise mask instead of Bayer's grid — an organic, gridless stipple that reads like fine film grain rather than cross-hatch.",
};
// Error-diffusion styles are the heavy ones: they run serially (each pixel depends on its neighbours'
// rounding error), so live preview on the camera is noticeably slower (~4–6 fps vs ~10+ for the
// others). The picker brackets them off with a "slower" divider so the trade-off is visible up front.
const HEAVY_STYLES = new Set(["Floyd", "Atkinson", "Sierra"]);

/** Case-insensitive name sort for the preset picker dropdowns (the randomiser pool stays unsorted). */
const byName = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

/** The portal's editing model. (#105 maps this to the Contract's Look wire format.) */
export interface EditableLook {
  /** The committed Look's server-minted immutable id (32 hex), when this Look was
   *  loaded from the library. Absent for a fresh/never-committed Look — the server
   *  mints one on first commit. Threaded back on commit so an edit updates in place
   *  (#161). */
  id?: string;
  name: string;
  style: number; // 0..9 (ditherCycle index)
  colors: string[]; // "#rrggbb"
  mapMode: MapMode; // "nearest" | "luminance" | "gradient"
  brightness: number; // -100..100
  contrast: number;
  saturation: number;
  // Per-Look tone ops (ADR-0033, #164 P4) — committed + synced (graduated from the preview pilot).
  gamma: number; // tone curve, PERCENT 50..200; 100 = identity
  posterize: number; // N tone levels/channel, 0 = off, else 2..8
  hueRotate: number; // hue rotation, degrees -180..180; 0 = identity
  negative: boolean;
  solarize: boolean;
  // Per-Look dither knobs (ADR-0033, #164 P5) — committed + synced (graduated from the preview pilot).
  ditherStrength: number; // dither amplitude, PERCENT 0..200; 100 = native
  ditherBias: number; // threshold lean, -64..64; 0 = none
  glyphSet: "dots" | "blocks" | "lines" | "characters" | "characters16"; // ASCII glyph ramp
  glyphSize: number; // glyph cell scale, PERCENT 50..300; 100 = native
  // Luma Key / Chroma Key / Splash (#235, ADR-0043) — committed Look fields, synced to the
  // camera like every other field (lookWire writes them into the wire palette).
  lumaKey?: number; // luma-key threshold 0..255; 0/absent = off
  chromaKey?: number; // chroma-key threshold 0..255; 0/absent = off
  splash?: number; // nearest-mode splash 0..100; 0/absent = off
  vignette?: number; // #238 signed strength -100..100; + darkens rim (spotlight), - brightens (halo)
  vignetteRadius?: number; // 0..100, size of the untouched centre
  vignetteFeather?: number; // 0..100, softness of the clean->burn transition
}
export interface LookSource {
  data: Uint8ClampedArray;
  w: number;
  h: number;
}

export const DEFAULT_LOOK: EditableLook = {
  name: "Untitled",
  style: 1, // Bayer
  colors: ["#0a0d0a", "#2c5663", "#78c846", "#c8ebd2"],
  mapMode: "luminance",
  brightness: 0,
  contrast: 0,
  saturation: 0,
  gamma: 100, // identity (percent)
  posterize: 0, // off
  hueRotate: 0,
  negative: false,
  solarize: false,
  ditherStrength: 100, // identity (percent)
  ditherBias: 0, // no lean
  glyphSet: "dots",
  glyphSize: 100, // native glyph dim (percent)
  lumaKey: 0, // selective keys (#235, ADR-0043) — off
  chromaKey: 0,
  splash: 0,
  vignette: 0, vignetteRadius: 0, vignetteFeather: 0, // #238 vignette — off
};

/** A built-in demo scene so there's a meaningful preview before any upload. */
function demoSource(w: number, h: number): LookSource {
  const data = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const v = Math.sin(x * 0.05) + Math.sin(y * 0.04) + Math.sin((x + y) * 0.03) + Math.sin(Math.hypot(x - w / 2, y - h / 2) * 0.06);
      data[i] = 128 + 90 * Math.sin(v);
      data[i + 1] = 128 + 90 * Math.sin(v + 2.1);
      data[i + 2] = 128 + 90 * Math.sin(v + 4.2);
      data[i + 3] = 255;
    }
  return { data, w, h };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    // R25: release the object URL as soon as the browser has decoded (or failed to
    // decode) the image, so a long editing session doesn't pin every uploaded (multi-MB)
    // blob for the page lifetime — mirroring download.ts's revoke-after-use.
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("failed to load image")); };
    img.src = url;
  });
}
/** Load an image from a (cross-origin presigned R2) URL — the Reference-loop variant of
 *  loadImage. `crossOrigin = "anonymous"` keeps the canvas untainted so imageToSource can
 *  read its pixels back. */
function loadImageUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}
/** Cover-crop an image to a square `size`×`size` RGBA source (browser-only). */
function imageToSource(img: HTMLImageElement, size: number): LookSource {
  const cv = document.createElement("canvas");
  cv.width = size;
  cv.height = size;
  const ctx = cv.getContext("2d")!;
  const s = Math.max(size / img.width, size / img.height);
  const dw = img.width * s, dh = img.height * s;
  ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
  return { data: ctx.getImageData(0, 0, size, size).data, w: size, h: size };
}
/** Scale an image to fit within `max`×`max` (NO crop) — samples the WHOLE image, for palette
 *  extraction from a reference (unlike imageToSource, which cover-crops for the square preview). */
function imageToFullSource(img: HTMLImageElement, max: number): LookSource {
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext("2d")!;
  ctx.drawImage(img, 0, 0, w, h);
  return { data: ctx.getImageData(0, 0, w, h).data, w, h };
}

/** An animated source: cover-cropped RGBA frames + their per-frame delays (seconds). */
interface AnimatedSource {
  frames: LookSource[];
  delays: number[];
}

/** Reconstruct a decoded GIF frame's palette indices into a cover-cropped square RGBA source. */
function gifFrameToSource(frame: GifFrame, size: number): LookSource {
  const { width: w, height: h, palette, indices } = frame;
  const native = new Uint8ClampedArray(w * h * 4);
  for (let k = 0; k < w * h; k++) {
    const at = indices[k]! * 3;
    native[k * 4] = palette[at] ?? 0;
    native[k * 4 + 1] = palette[at + 1] ?? 0;
    native[k * 4 + 2] = palette[at + 2] ?? 0;
    native[k * 4 + 3] = 255;
  }
  const src = document.createElement("canvas");
  src.width = w; src.height = h;
  const sctx = src.getContext("2d")!;
  const id = sctx.createImageData(w, h);
  id.data.set(native);
  sctx.putImageData(id, 0, 0);
  const dst = document.createElement("canvas");
  dst.width = size; dst.height = size;
  const dctx = dst.getContext("2d")!;
  const s = Math.max(size / w, size / h);
  dctx.drawImage(src, (size - w * s) / 2, (size - h * s) / 2, w * s, h * s);
  return { data: dctx.getImageData(0, 0, size, size).data, w: size, h: size };
}

/** N evenly-spaced grays (black→white) — the "dithered before the palette" tone ramp. */
function grayscalePalette(n: number): string[] {
  if (n <= 1) return ["#808080"];
  return Array.from({ length: n }, (_, i) => {
    const h = Math.round((i * 255) / (n - 1)).toString(16).padStart(2, "0");
    return `#${h}${h}${h}`;
  });
}

/** A 4×4×4 even RGB cube (64 colours) — the full-colour reference for the Nearest-colour
 *  Dither view, so it shows the dithered colours your palette snaps to (not tones). */
function colorCubePalette(): string[] {
  const lv = [0, 85, 170, 255];
  const hx = (n: number): string => n.toString(16).padStart(2, "0");
  const out: string[] = [];
  for (const r of lv) for (const g of lv) for (const b of lv) out.push(`#${hx(r)}${hx(g)}${hx(b)}`);
  return out;
}

export interface LookPortalProps {
  initialLook?: EditableLook;
  /** Injectable source (tests); the app uses the demo scene + uploads. */
  source?: LookSource;
  /** Re-seed the editor with a committed Look loaded from the library (a new object reference
   *  each load re-applies it; undefined leaves the editor alone). Preserves any uploaded source. */
  loadLook?: EditableLook;
  /** A gallery Capture/Clip sent in as the editor's source/palette seed (a fresh object re-loads it).
   *  `isClip` decodes it as an animated GIF (all frames); otherwise it's a single still. */
  seed?: { url: string; isClip?: boolean };
  /** Fetch the selected camera's Reference loop (PRD #106) as a list of presigned frame URLs —
   *  wired by LooksPage when a camera is selected (else undefined hides the "Camera loop" source).
   *  Resolves to [] when the camera hasn't recorded a loop yet; rejects on a transport error. */
  onRequestCameraLoop?: () => Promise<{ url: string }[]>;
  /** Name of the camera the loop comes from, for the source caption (e.g. "Cam One"). */
  cameraName?: string;
  /** The "ship bar" (camera picker + Commit) — wired by LooksPage so committing stays in view while
   *  authoring. Omitted in unit tests, where the bar isn't rendered. */
  devices?: Device[];
  deviceId?: string;
  onSelectDevice?: (id: string) => void;
  onCommit?: () => void;
  /** Start a brand-new Look (clears the editor to defaults, no committed id) — routed through the
   *  parent's unsaved-edits guard. Without this the editor only ever edits the loaded Look. */
  onNewLook?: () => void;
  committing?: boolean;
  commitStatus?: { kind: "ok" | "error"; text: string } | null;
  /** The editor differs from the last committed/loaded baseline (LooksPage computes it) —
   *  the Commit button wears a visible pending-changes marker so commit-vs-preview state
   *  is legible at a glance, not only when a load tries to replace the edits. */
  dirty?: boolean;
  /** Tethered Test-look mirror status — shown near the preview so the live-mirror is visible. */
  testLookState?: "idle" | "saving" | "saved" | "failed";
  /** Reports the Look on every edit (and once on mount). */
  onLookChange?: (look: EditableLook) => void;
}

// Remember the last UPLOADED source (especially a GIF) across reloads, so iterating on effects
// doesn't mean re-uploading every time. We store the ORIGINAL file bytes (data URL) — compact and
// re-decoded on mount via the Upload path; storing decoded RGBA frames would blow the ~5MB quota.
const SOURCE_KEY = "pixie:editorSource:v1";
const SOURCE_MAX_BYTES = 4 * 1024 * 1024; // base64 inflates ~33%; keep clear of the quota
interface SavedSource { name: string; dataUrl: string; }
/** One pane of the enlarge-preview lightbox: which preview canvas to mirror + its label. */
interface ZoomPane { ref: { current: HTMLCanvasElement | null }; caption: string; }

function persistSource(file: File): void {
  try {
    if (file.size > SOURCE_MAX_BYTES) { localStorage.removeItem(SOURCE_KEY); return; }
  } catch { return; }
  const reader = new FileReader();
  reader.onload = () => {
    try {
      localStorage.setItem(SOURCE_KEY, JSON.stringify({ name: file.name, dataUrl: String(reader.result) }));
    } catch { /* quota exceeded — keep whatever was there before */ }
  };
  reader.readAsDataURL(file);
}

export function LookPortal({ initialLook, source: injectedSource, loadLook, seed, onRequestCameraLoop, cameraName, devices = [], deviceId = "", onSelectDevice, onCommit, onNewLook, committing = false, commitStatus, dirty = false, testLookState = "idle", onLookChange }: LookPortalProps): React.JSX.Element {
  const [look, setLook] = useState<EditableLook>(initialLook ?? DEFAULT_LOOK);
  // R27: stable identity per palette swatch, kept in lockstep with `look.colors`. Per-swatch
  // ops (add / remove / drag-reorder) mutate this in the same shape; a wholesale palette swap
  // re-seeds it (regenColorIds). Editing a colour's VALUE leaves its id untouched, so the open
  // colour-picker stays bound to the swatch you grabbed.
  const [colorIds, setColorIds] = useState<string[]>(() => (initialLook ?? DEFAULT_LOOK).colors.map(mkColorId));
  const regenColorIds = (cs: readonly string[]): void => setColorIds(cs.map(mkColorId));
  // Which dither style the help panel describes — the hovered/focused chip, else the selected one.
  const [hoveredStyle, setHoveredStyle] = useState<number | null>(null);
  // The animated motion source: an uploaded image/GIF, or the camera's decoded Reference loop.
  const [uploaded, setUploaded] = useState<AnimatedSource | null>(null);
  // The pending camera Reference loop (frame URLs) — set by the Camera-loop button, decoded by an effect.
  const [referenceLoop, setReferenceLoop] = useState<{ url: string }[] | null>(null);
  // A short label for what the previews are showing (Demo / a filename / Camera loop / a load error).
  const [sourceCaption, setSourceCaption] = useState("Demo scene");
  const [srcBusy, setSrcBusy] = useState(false); // a Camera-loop fetch is in flight
  const [sourceKind, setSourceKind] = useState<"demo" | "upload" | "loop" | "capture" | "live">("demo"); // active source
  const [frameIdx, setFrameIdx] = useState(0);
  const [userPresets, setUserPresets] = useState<PalettePreset[]>(() => loadUserPresets());
  // Which swatch the in-house ColorPicker is editing (null = closed). Positional is safe:
  // the modal traps interaction, so the palette can't reorder underneath it.
  const [pickerIdx, setPickerIdx] = useState<number | null>(null);

  // Mobile Look-PiP drag (owner request): the floating orb can be parked anywhere — any
  // fixed corner covers SOMETHING eventually. Dragging swaps the CSS corner default for
  // inline fixed coords (clamped to the viewport); a real tap (< 6px travel) still zooms.
  // Desktop is untouched: the guard only engages where the PiP CSS exists (≤640px).
  const [pipStyle, setPipStyle] = useState<React.CSSProperties | undefined>(undefined);
  const pipDrag = useRef<{ startX: number; startY: number; baseX: number; baseY: number; moved: boolean } | null>(null);
  const pipClickSuppress = useRef(false);
  function pipActive(): boolean {
    return typeof window.matchMedia === "function" && window.matchMedia("(max-width: 640px)").matches;
  }
  function onPipPointerDown(e: React.PointerEvent<HTMLElement>): void {
    if (!pipActive()) return;
    const rect = e.currentTarget.getBoundingClientRect();
    pipDrag.current = { startX: e.clientX, startY: e.clientY, baseX: rect.left, baseY: rect.top, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onPipPointerMove(e: React.PointerEvent<HTMLElement>): void {
    const d = pipDrag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < 6) return; // tap tolerance — a jittery tap still zooms
    d.moved = true;
    const el = e.currentTarget;
    const x = Math.min(Math.max(d.baseX + dx, 4), window.innerWidth - el.offsetWidth - 4);
    const y = Math.min(Math.max(d.baseY + dy, 4), window.innerHeight - el.offsetHeight - 4);
    setPipStyle({ left: x, top: y, right: "auto", bottom: "auto" });
  }
  function onPipPointerUp(): void {
    if (pipDrag.current?.moved) pipClickSuppress.current = true; // the drag's click must not zoom
    pipDrag.current = null;
  }
  // The active palette's display name + whether it's been hand-edited since loading.
  const [paletteName, setPaletteName] = useState("Custom");
  const [paletteEdited, setPaletteEdited] = useState(false);
  const [removedColors, setRemovedColors] = useState<string[]>([]); // − color pushes here; + color restores
  // Raw text in the "From image" colour-count field. Kept as a string so the field can be
  // cleared and freely retyped — a number-typed controlled value snapped any empty/partial
  // input back to the minimum on every keystroke. Clamped to [2, MAX] only at derive time / blur.
  const [deriveCount, setDeriveCount] = useState("6"); // how many colours "From image" extracts
  const [paletteRef, setPaletteRef] = useState<LookSource | null>(null); // optional reference image to pull from
  // Snapshot taken right before a Random look, so a single Undo restores the prior Look.
  const [preRandom, setPreRandom] = useState<{ look: EditableLook; paletteName: string; paletteEdited: boolean } | null>(null);
  const [deriveMsg, setDeriveMsg] = useState<string | null>(null); // feedback for the Build-from-image action
  // While an Adjust number field is being typed into, hold its raw text (keyed by field) so it can
  // briefly be "" or "-" mid-edit without the controlled numeric value snapping it back. Committed to
  // look[k] only once the text parses to a number; cleared on blur so the field re-shows the clamped value.
  const [adjustDraft, setAdjustDraft] = useState<Record<string, string>>({});
  // Gradient (authored-order ramp) is one of the three Palette mapping MODES (Nearest / Luminance /
  // Gradient), unified into look.mapMode — no separate gradient toggle.
  // The glyph-size knob is ASCII-only — show the resulting cell px alongside the percent when ASCII is
  // active. The native glyph dim is 8 for the typed-character ramps, 5 for the dot ramps.
  const isAscii = STYLE_NAMES[look.style] === "ASCII";
  const asciiCellPx = Math.max(2, Math.min(24, Math.round((look.glyphSet.startsWith("characters") ? 8 : 5) * look.glyphSize / 100)));
  // The committed tone + dither ops, applied to BOTH preview canvases so the Dither reference shows the
  // same dither + adjustments (incl. glyph set/size, strength/bias) as the Look. Everything now comes
  // from the committed `look` (graduated, #164 P4 tone + P5 dither) — nothing is preview-only anymore.
  // gamma + ditherStrength + glyphSize are PERCENT and pass straight through (the twin does its own
  // /100), so DON'T divide here.
  const previewFx = {
    gamma: look.gamma, negative: look.negative, solarize: look.solarize,
    posterize: look.posterize, hueRotate: look.hueRotate,
    ditherStrength: look.ditherStrength, ditherBias: look.ditherBias,
    glyphSet: look.glyphSet, glyphSize: look.glyphSize,
    lumaKey: look.lumaKey ?? 0, chromaKey: look.chromaKey ?? 0, splash: look.splash ?? 0, // selective keys (#235)
    vignette: look.vignette ?? 0, vignetteRadius: look.vignetteRadius ?? 0, vignetteFeather: look.vignetteFeather ?? 0, // #238 vignette
  };
  // The Dither·Colour reference Look: same style + adjustments as the Look, but a reference palette
  // (colour cube for Nearest, else a grayscale ramp) — the dither structure BEFORE your palette. This is
  // also the live "Dither·Colour" source (#262); it mirrors the static grayCanvasRef effect below.
  const refLook = useMemo<EditableLook>(
    () => ({ ...look, colors: look.mapMode === "nearest" ? colorCubePalette() : grayscalePalette(look.colors.length) }),
    [look],
  );
  // Click-to-enlarge: mirror a preview canvas into a big lightbox canvas so you can inspect the
  // dither pixel-by-pixel (scaled nearest-neighbour). Live (not a snapshot), so GIF sources keep
  // animating and edits reflect immediately.
  const [zoom, setZoom] = useState<{ panes: ZoomPane[] } | null>(null);
  const zoomCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  // Open the lightbox. For the Dither/Look previews, show the raw Source beside it (A/B compare);
  // clicking Source shows it alone (comparing it to itself is pointless).
  const openZoom = (ref: { current: HTMLCanvasElement | null }, caption: string): void => {
    if (!ref.current) return;
    // Live mode has no Source orb, so a click enlarges only the clicked orb (no A/B-with-Source pane).
    const panes: ZoomPane[] = ref === rawCanvasRef || sourceKind === "live"
      ? [{ ref, caption }]
      : [{ ref: rawCanvasRef, caption: "Source" }, { ref, caption }];
    zoomCanvasRefs.current = [];
    setZoom({ panes });
  };
  const demo = useMemo(() => demoSource(VIEW, VIEW), []);
  const frames = uploaded?.frames ?? null;
  const source: LookSource = injectedSource ?? frames?.[Math.min(frameIdx, frames.length - 1)] ?? demo;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rawCanvasRef = useRef<HTMLCanvasElement>(null);
  const grayCanvasRef = useRef<HTMLCanvasElement>(null);
  const refCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    onLookChange?.(look);
  }, [look, onLookChange]);

  // Re-seed the editor when a committed Look is loaded from the library (#105 follow-up). Each
  // load hands a fresh object (getLook → wireLookToEditable), so re-opening the same Look re-fires;
  // undefined on mount → no-op. Doesn't touch the uploaded source, so the preview keeps your photo.
  useEffect(() => {
    if (loadLook) {
      setLook(loadLook);
      setPaletteName(loadLook.name);
      setPaletteEdited(false);
      setRemovedColors([]); // wholesale palette swap — drop the prior palette's +/- undo stack
      setColorIds(loadLook.colors.map(mkColorId)); // R27: fresh palette → fresh swatch ids
    }
  }, [loadLook]);

  // R27 safety net: keep the swatch-id list the same length as the palette even if some
  // future colours mutation forgets to maintain it — surviving slots keep their id, new
  // slots get a fresh one. Reorders (same length) are handled precisely in reorderColor.
  useEffect(() => {
    setColorIds((ids) =>
      ids.length === look.colors.length
        ? ids
        : look.colors.map((_, j) => ids[j] ?? mkColorId()),
    );
    // Intentionally keyed off the count only — reorders (same length) are handled precisely
    // in reorderColor, and reacting to `look.colors` identity would re-seed ids on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [look.colors.length]);

  // Decode the camera's Reference loop (PRD #106) into the animated motion source — the substrate
  // the owner designs a Look against. `referenceLoop` is set by the Camera-loop source button. Each
  // frame's presigned URL is cover-cropped to the square View (imageToSource), then assembled into
  // an AnimatedSource with even ~3fps delays so the existing GIF playback animates it.
  // Frames load + decode in PARALLEL (a sequential per-frame await was the multi-second wait); the
  // preview lights up the moment the FIRST frame decodes, and the caption shows live progress, so it
  // feels instant while the rest stream in. A failed frame is dropped, not fatal. Guarded like the
  // canvas effects: jsdom has no real Image decoding, so a hung load leaves the editor alone.
  useEffect(() => {
    if (!referenceLoop || referenceLoop.length === 0) return;
    let live = true;
    const total = referenceLoop.length;
    let done = 0;
    let shownFirst = false;
    const jobs = referenceLoop.map(({ url }) =>
      loadImageUrl(url)
        .then((img) => {
          if (!live) return null;
          const src = imageToSource(img, VIEW);
          done += 1;
          if (!shownFirst) {
            shownFirst = true; // light up the round preview as soon as ANY frame is ready
            setUploaded({ frames: [src], delays: [0] });
            setFrameIdx(0);
          }
          setSourceCaption(`Loading camera loop… ${done}/${total}`);
          return src;
        })
        .catch(() => null),
    );
    (async () => {
      const settled = await Promise.all(jobs);
      if (!live) return;
      const frames = settled.filter((s): s is LookSource => s !== null);
      if (frames.length === 0) {
        setSourceCaption("Couldn't load the camera loop — try again.");
        return;
      }
      setUploaded({ frames, delays: frames.map(() => 1 / 3) }); // ~3fps, even — the full ordered loop
      setFrameIdx(0);
      setSourceCaption(`Camera loop · ${frames.length} frames${cameraName ? ` · ${cameraName}` : ""}`);
    })();
    return () => { live = false; };
  }, [referenceLoop, cameraName]);

  // Load a gallery Capture/Clip ("Use in editor") as the source — a fresh `seed` reference re-loads
  // it, so the owner can Derive a palette from their own media. A Clip is a GIF: fetch its bytes and
  // decode ALL frames (like the Upload path), so it animates instead of freezing on frame 0; a still
  // Capture loads as one image. Guarded: a decode failure (expired URL, or jsdom) leaves the editor alone.
  useEffect(() => {
    if (!seed) return;
    let live = true;
    setReferenceLoop(null);
    setSourceCaption("Loading capture…");
    setSourceKind("capture");
    (async () => {
      try {
        if (seed.isClip) {
          const buf = await (await fetch(seed.url)).arrayBuffer();
          if (!live) return;
          const decoded = decodeGif(buf, 120); // cap a long Clip
          if (decoded.length > 0) {
            setUploaded({
              frames: decoded.map((fr) => gifFrameToSource(fr, VIEW)),
              delays: decoded.map((fr) => fr.delaySeconds),
            });
            setSourceCaption(`From a clip · ${decoded.length} frames`);
            return;
          }
        }
        const img = await loadImageUrl(seed.url);
        if (!live) return;
        setUploaded({ frames: [imageToSource(img, VIEW)], delays: [0] });
        setSourceCaption("From a capture");
      } catch {
        if (live) setSourceCaption("Couldn't load that capture — it may have expired.");
      }
    })();
    return () => { live = false; };
  }, [seed]);

  // Restore the last uploaded source from localStorage on mount, so a reload keeps your GIF/image
  // (persistSource saved the original bytes). Skipped when a `seed` is present — an explicit
  // "Use in editor" wins. Mount-only; re-decoded via the same path as Upload.
  useEffect(() => {
    if (seed) return;
    let raw: string | null = null;
    try { raw = localStorage.getItem(SOURCE_KEY); } catch { return; }
    if (!raw) return;
    let saved: SavedSource;
    try { saved = JSON.parse(raw) as SavedSource; } catch { return; }
    if (!saved?.dataUrl) return;
    let live = true;
    setSourceKind("upload");
    setSourceCaption(`Restoring ${saved.name}…`);
    (async () => {
      try {
        if (saved.dataUrl.startsWith("data:image/gif")) {
          const buf = await (await fetch(saved.dataUrl)).arrayBuffer();
          if (!live) return;
          const decoded = decodeGif(buf, 120);
          if (decoded.length > 0) {
            setUploaded({ frames: decoded.map((fr) => gifFrameToSource(fr, VIEW)), delays: decoded.map((fr) => fr.delaySeconds) });
            setSourceCaption(`${saved.name} · restored`);
            return;
          }
        }
        const img = await loadImageUrl(saved.dataUrl);
        if (!live) return;
        setUploaded({ frames: [imageToSource(img, VIEW)], delays: [0] });
        setSourceCaption(`${saved.name} · restored`);
      } catch {
        if (live) { setSourceKind("demo"); setSourceCaption("Demo scene"); }
      }
    })();
    return () => { live = false; };
    // mount-only restore; `seed` is read once to defer to an explicit Use-in-editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Animate an uploaded GIF: advance the shown frame by real elapsed time, looping at each
  // frame's GCE delay (the shared frameForTime). setFrameIdx is a no-op when the index is
  // unchanged, so previewLook only re-runs on an actual frame change (or a Look edit), not
  // every rAF. A still image (one frame) never animates.
  useEffect(() => {
    if (!uploaded || uploaded.frames.length < 2) { setFrameIdx(0); return; }
    // R28: the timeline helper only needs each frame's delay (FrameTiming), so hand it a
    // plain delays list — no fabricating partial GifFrame objects behind an unsafe cast.
    const timing: FrameTiming[] = uploaded.delays.map((d) => ({ delaySeconds: d }));
    const start = performance.now();
    let raf = 0;
    const tick = (now: number): void => {
      setFrameIdx(frameForTime(timing, (now - start) / 1000));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [uploaded]);

  // Faithful preview — reduce to RGB565, run the twin, draw the RGBA. Guarded so the
  // component renders under jsdom (no 2D context there); the camera-faithful pixels
  // are proven in cores/preview.test.ts, so the test asserts the controls, not pixels.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || sourceKind === "live") return; // live source drives this orb via the worker (#262)
    // R24: coalesce the heavy dither into ONE render on the next animation frame. A
    // slider drag or GIF frame advance fires many look/source changes in quick
    // succession; scheduling into rAF (and cancelling any pending one on re-run/unmount)
    // collapses a burst to a single dither instead of running it synchronously on every
    // change. Scheduling only — the drawn pixels are byte-identical to a synchronous
    // render of the same (look, source); the dither twin stays golden-pinned.
    let raf = requestAnimationFrame(() => {
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      const palette = paletteFromHex({
        colors: look.colors,
        mapMode: look.mapMode,
        brightness: look.brightness,
        contrast: look.contrast,
        saturation: look.saturation,
        ...previewFx,
      });
      const rgba = previewLook(source.data, source.w, source.h, look.style, palette);
      cv.width = source.w;
      cv.height = source.h;
      const img = ctx.createImageData(source.w, source.h);
      img.data.set(rgba);
      ctx.putImageData(img, 0, 0);
    });
    return () => cancelAnimationFrame(raf);
  }, [look, source, sourceKind]);

  // Escape closes the enlarge-preview lightbox (registered only while it's open).
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setZoom(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  // Live-mirror the chosen preview canvas into the lightbox canvas each frame (keeps GIF sources
  // animating + reflects edits). Canvas-to-canvas drawImage never taints, so cross-origin sources
  // are fine. Nearest-neighbour upscale (imageSmoothingEnabled=false) keeps the dither crisp.
  useEffect(() => {
    if (!zoom) return;
    let raf = 0;
    const draw = (): void => {
      zoom.panes.forEach((pane, i) => {
        const src = pane.ref.current, dst = zoomCanvasRefs.current[i];
        if (src && dst) {
          if (dst.width !== src.width || dst.height !== src.height) { dst.width = src.width; dst.height = src.height; }
          const ctx = dst.getContext("2d");
          if (ctx) { ctx.imageSmoothingEnabled = false; ctx.clearRect(0, 0, dst.width, dst.height); ctx.drawImage(src, 0, 0); }
        }
      });
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [zoom]);

  // The raw source view (un-dithered) — see the input pixels next to the result, so you
  // can imagine how a palette will map while composing it. Tracks the same animated source.
  useEffect(() => {
    const cv = rawCanvasRef.current;
    if (!cv || sourceKind === "live") return; // Source orb is hidden in live mode (#262)
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    cv.width = source.w;
    cv.height = source.h;
    const img = ctx.createImageData(source.w, source.h);
    img.data.set(source.data);
    ctx.putImageData(img, 0, 0);
  }, [source, sourceKind]);

  // "Dithered before the palette" — the same dither + adjustments mapped onto a grayscale
  // ramp (one gray per palette color), so you see the dither texture/tones your colors fill.
  useEffect(() => {
    const cv = grayCanvasRef.current;
    if (!cv || sourceKind === "live") return; // live source drives this orb via the worker (#262)
    // R24: same rAF coalescing as the main preview — this reference canvas ran a SECOND
    // full dither synchronously on every look/source change; defer + drop stale frames so
    // a drag no longer fires two synchronous dithers per tick. Scheduling only (byte-exact).
    let raf = requestAnimationFrame(() => {
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      // Reference matched to the mapping mode: a grayscale ramp for Brightness-ramp (the tones
      // your colours fill), or a full-colour cube for Nearest-colour (the dithered colours your
      // palette snaps to). Either way it's the dither structure, before YOUR palette.
      const refPalette = paletteFromHex({
        colors: look.mapMode === "nearest" ? colorCubePalette() : grayscalePalette(look.colors.length),
        mapMode: look.mapMode,
        brightness: look.brightness,
        contrast: look.contrast,
        saturation: look.saturation,
        ...previewFx,
      });
      const rgba = previewLook(source.data, source.w, source.h, look.style, refPalette);
      cv.width = source.w;
      cv.height = source.h;
      const img = ctx.createImageData(source.w, source.h);
      img.data.set(rgba);
      ctx.putImageData(img, 0, 0);
    });
    return () => cancelAnimationFrame(raf);
  }, [look, source, sourceKind]);

  // Thumbnail of the uploaded reference image (the palette source), so you can see what you loaded.
  useEffect(() => {
    const cv = refCanvasRef.current;
    if (!cv || !paletteRef) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    cv.width = paletteRef.w;
    cv.height = paletteRef.h;
    const img = ctx.createImageData(paletteRef.w, paletteRef.h);
    img.data.set(paletteRef.data);
    ctx.putImageData(img, 0, 0);
  }, [paletteRef]);

  function patch(p: Partial<EditableLook>): void {
    setLook((prev) => ({ ...prev, ...p }));
  }
  /** Palette edits mark it hand-edited (so the name shows "unsaved changes"). */
  function setColors(fn: (prev: string[]) => string[]): void {
    setPaletteEdited(true);
    setLook((prev) => ({ ...prev, colors: fn(prev.colors) }));
  }
  /** Remove colour `i` (any swatch, not just the last), remembering it so + restores it (not white). */
  function removeColorAt(i: number): void {
    if (look.colors.length <= 1) return;
    const removed = look.colors[i];
    if (removed !== undefined) setRemovedColors((r) => [...r, removed]);
    setColors((cs) => cs.filter((_, j) => j !== i));
    setColorIds((ids) => ids.filter((_, j) => j !== i)); // R27: drop the removed swatch's id
  }
  /** + color: restore the most-recently-removed colour if there is one, else add white. */
  function addColor(): void {
    if (look.colors.length >= MAX_COLORS_PER_PALETTE) return;
    const restored = removedColors[removedColors.length - 1];
    if (restored !== undefined) setRemovedColors((r) => r.slice(0, -1));
    setColors((cs) => [...cs, restored ?? "#ffffff"]);
    setColorIds((ids) => [...ids, mkColorId()]); // R27: the new swatch gets a fresh id
  }
  // Drag-reorder swatches — the colour order is what Gradient-ramp mode walks brightness through.
  const dragIndex = useRef<number>(-1);
  function reorderColor(from: number, to: number): void {
    if (from === to || from < 0 || to < 0) return;
    setColors((cs) => {
      const next = cs.slice();
      const [moved] = next.splice(from, 1);
      if (moved === undefined) return cs;
      next.splice(to, 0, moved);
      return next;
    });
    // R27: move the swatch's id with its colour so React follows the DOM node (not the slot).
    setColorIds((ids) => {
      if (from >= ids.length || to >= ids.length) return ids;
      const next = ids.slice();
      const [moved] = next.splice(from, 1);
      if (moved === undefined) return ids;
      next.splice(to, 0, moved);
      return next;
    });
  }
  function setMapMode(mode: MapMode): void {
    setPaletteEdited(true);
    patch({ mapMode: mode });
  }

  /** Load a preset's colors + adjustments into the current Look. */
  function applyPreset(p: PalettePreset): void {
    setPaletteName(p.name);
    setPaletteEdited(false);
    setRemovedColors([]); // a wholesale palette load invalidates the undo stack
    regenColorIds(p.colors); // R27: fresh palette → fresh swatch ids
    patch({
      colors: [...p.colors],
      mapMode: p.mapMode,
      brightness: p.brightness,
      contrast: p.contrast,
      saturation: p.saturation,
    });
  }
  /** Load a starter Look — a complete default Look (ADR-0044) as a start-from template: sets the whole
   *  editor state (palette + style + effects), no id so Commit mints a fresh Look. */
  function applyStarterLook(sl: EditableLook): void {
    setLook({ ...sl, colors: [...sl.colors] });
    setPaletteName(sl.name);
    setPaletteEdited(false);
    setRemovedColors([]); // a wholesale Look load invalidates the +/− undo stack
    regenColorIds(sl.colors); // R27: fresh palette → fresh swatch ids
  }
  /** Load a reference image to pull a palette from — separate from the preview clip, and
   *  sampled whole (no crop). */
  async function onRefFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;
    setDeriveMsg("Loading reference image…");
    try {
      setPaletteRef(imageToFullSource(await loadImage(file), 256));
      setDeriveMsg(null);
    } catch {
      setDeriveMsg("Couldn't read that reference image — try another.");
    }
  }
  /** Derive `deriveCount` colours from the reference image (or the preview clip if none was
   *  uploaded). Palette-only — leaves the dither style + brightness/contrast/saturation alone;
   *  only the derived colours are snapped to what the camera can display. */
  function onDeriveFromImage(): void {
    const ref = paletteRef ?? source; // the reference image if uploaded, else the preview source
    const count = Math.max(2, Math.min(parseInt(deriveCount, 10) || 2, MAX_COLORS_PER_PALETTE));
    const colors = paletteFromImage(ref.data, ref.w, ref.h, count);
    if (colors.length === 0) {
      setDeriveMsg("Couldn't pull colours from that image — try another."); // honest, not a silent no-op
      return;
    }
    setRemovedColors([]); // a wholesale palette change invalidates the +/− undo stack
    setColors(() => colors); // replaces colors[]; setColors flips paletteEdited=true
    regenColorIds(colors); // R27: derived palette → fresh swatch ids
    setPaletteName("From image");
    setDeriveMsg(`Derived ${colors.length} colour${colors.length === 1 ? "" : "s"}.`);
  }
  /** Save/overwrite the current palette under its (editable) name to localStorage. */
  function onSavePalette(): void {
    const nm = paletteName.trim();
    if (!nm) return;
    setUserPresets(
      saveUserPreset({
        name: nm,
        colors: [...look.colors],
        mapMode: look.mapMode,
        brightness: look.brightness,
        contrast: look.contrast,
        saturation: look.saturation,
      }),
    );
    setPaletteName(nm);
    setPaletteEdited(false);
  }
  /** Delete the saved palette matching the current name (only if it's one of yours). */
  function onDeletePalette(): void {
    const nm = paletteName.trim();
    if (!userPresets.some((u) => u.name === nm)) return;
    setUserPresets(deleteUserPreset(nm));
    setPaletteEdited(true); // it's now an unsaved custom palette
  }
  /** Surprise me — a random palette + dither style AND randomized tone / dither / selective settings
   *  (each in a sensible range; the strong ones fire only sometimes). Snapshots first so it's undoable. */
  function onRandom(): void {
    setPreRandom({ look, paletteName, paletteEdited });
    setRemovedColors([]);
    const ri = (lo: number, hi: number): number => lo + Math.floor(Math.random() * (hi - lo + 1));
    const chance = (prob: number): boolean => Math.random() < prob;
    const pick = <T,>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)]!;

    const all = [...BUILTIN_PRESETS, ...userPresets];
    const p = all.length > 0 ? pick(all) : undefined;
    const mapMode = p ? p.mapMode : look.mapMode;
    // Roll the selective keys only under nearest mapping — but for TWO different reasons, because
    // the single reason this comment used to give was wrong and leaked into docs/effects.md and the
    // help text above. `splash` is genuinely inert elsewhere (it lives in the nearest branch of the
    // quantiser). `lumaKey`/`chromaKey` are NOT: they apply in every mapping mode. Outside nearest
    // they merely re-round luminance buckets instead of steering a region onto the greys, which is
    // not a look worth generating — so Random still skips them here, by choice rather than by
    // (mistaken) necessity. Only sometimes, either way, since they're strong.
    const nearest = mapMode === "nearest";
    const next: Partial<EditableLook> = {
      style: ri(0, STYLE_NAMES.length - 1),
      brightness: ri(-25, 25),
      contrast: ri(-15, 35),
      saturation: ri(-20, 40),
      gamma: chance(0.5) ? ri(70, 150) : 100,
      hueRotate: chance(0.5) ? ri(-45, 45) : 0,
      negative: chance(0.1),
      solarize: chance(0.12),
      ditherStrength: ri(70, 200),
      ditherBias: chance(0.4) ? ri(-30, 30) : 0,
      glyphSet: pick(["dots", "blocks", "lines", "characters", "characters16"] as const),
      glyphSize: ri(80, 180),
      lumaKey: nearest && chance(0.4) ? ri(90, 180) : 0,
      chromaKey: nearest && chance(0.4) ? ri(60, 140) : 0,
      splash: nearest && chance(0.2) ? ri(25, 55) : 0,
      vignette: chance(0.4) ? ri(-70, 90) : 0, // #238 vignette (works in any mapping)
      vignetteRadius: ri(0, 55),
      vignetteFeather: ri(20, 80),
    };
    if (p) {
      setPaletteName(p.name);
      setPaletteEdited(false);
      regenColorIds(p.colors); // R27: random palette → fresh swatch ids
      patch({ colors: [...p.colors], mapMode: p.mapMode, ...next });
    } else {
      patch(next); // no palette rolled — colours (and their ids) are unchanged
    }
  }
  /** Revert the last Random look to the snapshot taken just before it. */
  function onUndoRandom(): void {
    if (!preRandom) return;
    setLook(preRandom.look);
    setPaletteName(preRandom.paletteName);
    setPaletteEdited(preRandom.paletteEdited);
    setRemovedColors([]);
    regenColorIds(preRandom.look.colors); // R27: restored palette → fresh swatch ids
    setPreRandom(null);
  }

  /** Back to the built-in demo scene (clears any uploaded image / camera loop). */
  function useDemo(): void {
    setUploaded(null);
    setReferenceLoop(null);
    setFrameIdx(0);
    setSourceCaption("Demo scene");
    setSourceKind("demo");
  }
  /** Load the selected camera's Reference loop as the motion source (PRD #106). Honest about the
   *  "no loop recorded yet" case and transport errors, right in the source caption (no silent no-op). */
  async function onCameraLoop(): Promise<void> {
    if (!onRequestCameraLoop) return;
    setSrcBusy(true);
    setSourceCaption("Loading camera loop…");
    try {
      const frames = await onRequestCameraLoop();
      if (frames.length === 0) {
        setSourceCaption("No camera loop yet — record one on the camera (Menu → Look Sync).");
        return;
      }
      setReferenceLoop(frames); // the decode effect streams the frames in + owns the caption from here
      setSourceKind("loop");
    } catch {
      setSourceCaption("Couldn't reach the camera loop — try again.");
    } finally {
      setSrcBusy(false);
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;
    setReferenceLoop(null);
    setSourceCaption(`Loading ${file.name}…`);
    setSourceKind("upload");
    try {
      if (file.type === "image/gif" || /\.gif$/i.test(file.name)) {
        const decoded = decodeGif(await file.arrayBuffer(), 120); // cap a long GIF
        if (decoded.length > 0) {
          setUploaded({
            frames: decoded.map((fr) => gifFrameToSource(fr, VIEW)),
            delays: decoded.map((fr) => fr.delaySeconds),
          });
          setSourceCaption(file.name);
          persistSource(file);
          return;
        }
      }
      setUploaded({ frames: [imageToSource(await loadImage(file), VIEW)], delays: [0] });
      setSourceCaption(file.name);
      persistSource(file);
    } catch {
      setSourceCaption(`Couldn't read “${file.name}” — try another image.`);
    }
  }

  const adjustKeys = ["brightness", "contrast", "saturation"] as const;
  const isSaved = userPresets.some((u) => u.name === paletteName.trim());

  return (
    <section className="lookportal">
      <div className="lookportal__head">
        <h2 className="lookportal__title">Look portal</h2>
        <div className="lookportal__head-actions">
          {preRandom && (
            <button type="button" className="lookportal__btn" onClick={onUndoRandom} data-testid="look-undo-random" title="Undo the last Random look">
              ↩ Undo
            </button>
          )}
          <button type="button" className="lookportal__btn lookportal__random" onClick={onRandom} data-testid="look-random">
            🎲 Random look
          </button>
        </div>
      </div>

      {onCommit && (
        <div className="lookportal__ship">
          {onNewLook && (
            <button
              type="button"
              className="lookportal__shipnew"
              onClick={onNewLook}
              title="Start a brand-new Look (a fresh blank look you then Commit as new)"
              data-testid="ship-new"
            >
              ✨ New Look
            </button>
          )}
          <label className="lookportal__field lookportal__shipname">
            Name
            <input value={look.name} onChange={(e) => patch({ name: e.target.value })} maxLength={31} data-testid="look-name" />
          </label>
          {devices.length > 0 ? (
            <>
              <label className="lookportal__field lookportal__shipcam">
                Camera
                <select value={deviceId} onChange={(e) => onSelectDevice?.(e.target.value)} data-testid="ship-device">
                  {devices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>{d.name ?? "Unnamed camera"}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className={`looklibrary__commit lookportal__shipcommit${dirty ? " lookportal__shipcommit--dirty" : ""}`}
                onClick={onCommit}
                disabled={committing}
                title={dirty ? "You have edits the camera doesn't have yet" : undefined}
                data-testid="ship-commit"
              >
                {committing ? "Committing…" : look.id ? `🎥 Update “${look.name}”` : `🎥 Commit new “${look.name}”`}
                {/* The pending-changes dot — visible dirty state, previously only surfaced by the
                    replace-your-edits guard when loading another Look. */}
                {dirty && !committing && (
                  <span className="lookportal__dirty-dot" aria-hidden="true" data-testid="ship-dirty" />
                )}
              </button>
            </>
          ) : (
            <p className="lookportal__hint">No claimed cameras yet — claim one to commit Looks.</p>
          )}
          {commitStatus && (
            <p
              className={`looklibrary__msg looklibrary__msg--${commitStatus.kind} lookportal__shipmsg`}
              role={commitStatus.kind === "error" ? "alert" : "status"}
              data-testid="ship-msg"
            >
              {commitStatus.text}
            </p>
          )}
        </div>
      )}

      <div className="lookportal__layout">
        <div className="lookportal__stage">
          <div className={`lookportal__views${sourceKind === "live" ? " lookportal__views--live" : ""}`}>
            <figure
              className="lookportal__view lookportal__view--zoom"
              style={{ display: sourceKind === "live" ? "none" : undefined }}
              role="button"
              tabIndex={0}
              aria-label="Enlarge the Source preview"
              onClick={() => openZoom(rawCanvasRef, "Source")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openZoom(rawCanvasRef, "Source"); } }}
            >
              <canvas ref={rawCanvasRef} className="lookportal__preview" width={VIEW} height={VIEW} data-testid="look-source" />
              <figcaption>Source</figcaption>
            </figure>
            <figure
              className="lookportal__view lookportal__view--zoom"
              role="button"
              tabIndex={0}
              aria-label="Enlarge the Dither preview"
              title={look.mapMode !== "nearest"
                ? "The dithered tones your palette colours map onto"
                : "The dithered full-colour image your palette snaps to its nearest"}
              onClick={() => openZoom(grayCanvasRef, look.mapMode !== "nearest" ? "Dither · tones" : "Dither · colour")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openZoom(grayCanvasRef, look.mapMode !== "nearest" ? "Dither · tones" : "Dither · colour"); } }}
            >
              <canvas ref={grayCanvasRef} className="lookportal__preview" width={VIEW} height={VIEW} data-testid="look-dither" />
              <figcaption>{look.mapMode !== "nearest" ? "Dither · tones" : "Dither · colour"}</figcaption>
            </figure>
            <figure
              className="lookportal__view lookportal__view--zoom"
              style={pipStyle}
              role="button"
              tabIndex={0}
              aria-label="Enlarge the Look preview"
              data-testid="look-pip"
              onPointerDown={onPipPointerDown}
              onPointerMove={onPipPointerMove}
              onPointerUp={onPipPointerUp}
              onClick={() => {
                if (pipClickSuppress.current) {
                  pipClickSuppress.current = false; // a drag just ended — not a zoom tap
                  return;
                }
                openZoom(canvasRef, "Look");
              }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openZoom(canvasRef, "Look"); } }}
            >
              <canvas ref={canvasRef} className="lookportal__preview" width={VIEW} height={VIEW} data-testid="look-preview" />
              <figcaption>Look</figcaption>
            </figure>
          </div>
          <div className="lookportal__source">
            <span className="lookportal__sublabel">Source</span>
            <div className="lookportal__srcrow">
              <button
                type="button"
                className={`lookportal__btn${sourceKind === "demo" ? " is-active" : ""}`}
                aria-pressed={sourceKind === "demo"}
                onClick={useDemo}
              >
                Demo
              </button>
              <label className={`lookportal__btn lookportal__filebtn${sourceKind === "upload" ? " is-active" : ""}`}>
                <span aria-hidden="true">⬆</span> Image / GIF
                <input type="file" accept="image/*" onChange={onFile} data-testid="look-upload" className="lookportal__filein" />
              </label>
              {onRequestCameraLoop && (
                <button
                  type="button"
                  className={`lookportal__btn${sourceKind === "loop" ? " is-active" : ""}`}
                  aria-pressed={sourceKind === "loop"}
                  onClick={onCameraLoop}
                  disabled={srcBusy}
                  data-testid="look-camera-loop"
                >
                  <span aria-hidden="true">📷</span> Camera loop
                </button>
              )}
              <button
                type="button"
                className={`lookportal__btn${sourceKind === "live" ? " is-active" : ""}`}
                aria-pressed={sourceKind === "live"}
                onClick={() => { setSourceKind("live"); setSourceCaption("Live camera"); }}
                data-testid="look-live-source"
              >
                <span aria-hidden="true">🔴</span> Live camera
              </button>
            </div>
            {sourceKind !== "live" && (
              <p className="lookportal__srccaption" data-testid="look-source-label">{sourceCaption}</p>
            )}
          </div>
          {sourceKind === "live" && (
            <LivePreviewPanel
              lookCanvasRef={canvasRef}
              ditherCanvasRef={grayCanvasRef}
              look={look}
              refLook={refLook}
              cameraName={cameraName}
            />
          )}
          {onCommit && cameraName && (
            <p
              className={`lookportal__live lookportal__live--${testLookState}`}
              aria-live="polite"
              data-testid="look-live"
              title="Your edits auto-save to this camera as a Test look — tap Look Sync on the camera to preview them live."
            >
              <span className="lookportal__live-dot" aria-hidden="true">●</span>
              {testLookState === "saving"
                ? `Mirroring to ${cameraName}…`
                : testLookState === "saved"
                  ? `Live to ${cameraName} · saved`
                  : testLookState === "failed"
                    ? `Live to ${cameraName} · save failed`
                    : `Live to ${cameraName}`}
            </p>
          )}
        </div>

        <div className="lookportal__controls">
          {/* Name lives in the ship bar (beside Commit) when wired; otherwise it lives here. */}
          {!onCommit && (
            <label className="lookportal__field">
              Name
              <input value={look.name} onChange={(e) => patch({ name: e.target.value })} maxLength={31} data-testid="look-name" />
            </label>
          )}

          <fieldset className="lookportal__group">
            <legend>Dither style</legend>
            <div className="lookportal__styles">
              {(() => {
                // Display order ≠ STYLE_NAMES order: render the FAST styles together first, then the
                // heavier serial error-diffusion group on its own line (each chip keeps its real engine
                // index `i`, so patch({style:i}) is unaffected — STYLE_NAMES order stays load-bearing).
                const chip = (styleName: string, i: number) => {
                  const heavy = HEAVY_STYLES.has(styleName);
                  return (
                    <button
                      key={styleName}
                      type="button"
                      aria-pressed={look.style === i}
                      className={`lookportal__chip${look.style === i ? " is-active" : ""}${heavy ? " lookportal__chip--heavy" : ""}`}
                      title={heavy ? "Error-diffusion — heavier: slower live preview (~4–6 fps), smoother gradients" : undefined}
                      onClick={() => patch({ style: i })}
                      onMouseEnter={() => setHoveredStyle(i)}
                      onMouseLeave={() => setHoveredStyle(null)}
                      onFocus={() => setHoveredStyle(i)}
                      onBlur={() => setHoveredStyle(null)}
                    >
                      {styleName}
                    </button>
                  );
                };
                const entries = STYLE_NAMES.map((n, i) => ({ n, i }));
                return (
                  <>
                    {entries.filter((e) => !HEAVY_STYLES.has(e.n)).map((e) => chip(e.n, e.i))}
                    <span className="lookportal__stylebreak" aria-hidden="true" />
                    <span className="lookportal__styletag" title="Error-diffusion styles run serially — slower live preview on the camera (~4–6 fps)">slower&nbsp;▸</span>
                    {entries.filter((e) => HEAVY_STYLES.has(e.n)).map((e) => chip(e.n, e.i))}
                  </>
                );
              })()}
            </div>
            <p className="lookportal__stylehelp" data-testid="look-style-help">
              <b>{STYLE_NAMES[hoveredStyle ?? look.style]}</b>
              {STYLE_HELP[STYLE_NAMES[hoveredStyle ?? look.style]!]}
            </p>
            <details className="lookportal__help">
              <summary>What is dithering?</summary>
              <p>{DITHER_INTRO}</p>
            </details>
          </fieldset>

          <fieldset className="lookportal__group">
            <legend>Palette</legend>
            <div className="lookportal__row lookportal__palette-bar">
              <label className="lookportal__field" style={{ flex: 1, minWidth: 0 }}>
                Palette name {paletteEdited && <em className="lookportal__dirty">· unsaved</em>}
                <input
                  value={paletteName}
                  data-testid="look-palette-name"
                  onChange={(e) => { setPaletteName(e.target.value); setPaletteEdited(true); }}
                />
              </label>
              <button
                type="button"
                className="lookportal__btn"
                title="Save this palette in your browser"
                onClick={onSavePalette}
                disabled={!paletteName.trim()}
              >
                {isSaved ? "Update" : "Save"}
              </button>
              {isSaved && (
                <button type="button" className="lookportal__btn lookportal__del" onClick={onDeletePalette}>Delete</button>
              )}
            </div>
            <p className="lookportal__hint">
              Palettes are saved in this browser. To send the whole Look to a camera, use <b>Commit</b> below.
            </p>
            {/* Starter Looks as visible palette-strip chips — pick by APPEARANCE, not by cryptic
                name in a dropdown (this is the fastest on-ramp to a great result and it was the
                least visible control in the editor). */}
            <div className="lookportal__field">
              <span>Start from a Look</span>
              <div className="lookportal__starters" role="group" aria-label="Starter Looks" data-testid="look-starters">
                {[...STARTER_LOOKS].sort(byName).map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    className="lookportal__starter"
                    onClick={() => applyStarterLook(s)}
                    title={`Start from “${s.name}”`}
                    data-testid={`starter-${s.name}`}
                  >
                    <span className="lookportal__starter-strip" aria-hidden="true">
                      {s.colors.slice(0, 9).map((c, i) => (
                        <i key={i} style={{ background: c }} />
                      ))}
                    </span>
                    <span className="lookportal__starter-name">{s.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <label className="lookportal__field">
              Load a palette
              <select
                aria-label="Load palette"
                data-testid="look-preset"
                value=""
                onChange={(e) => {
                  const p = [...userPresets, ...BUILTIN_PRESETS].find((pp) => pp.name === e.target.value);
                  if (p) applyPreset(p);
                }}
              >
                <option value="">Choose a palette…</option>
                {userPresets.length > 0 && (
                  <optgroup label="Mine">
                    {[...userPresets].sort(byName).map((p) => (
                      <option key={p.name} value={p.name}>{p.name}</option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Built-in">
                  {[...BUILTIN_PRESETS].sort(byName).map((p) => (
                    <option key={p.name} value={p.name}>{p.name}</option>
                  ))}
                </optgroup>
              </select>
            </label>
            <details className="lookportal__derive">
              <summary>Build from an image</summary>
              <p className="lookportal__hint">
                {paletteRef
                  ? "Pulls the most representative colours from your reference image."
                  : "Pick a reference image (or derive straight from the current preview), choose how many colours, then Derive."}
              </p>
              <label className="lookportal__filebtn lookportal__btn">
                <span aria-hidden="true">⬆</span> {paletteRef ? "Replace reference…" : "Reference image…"}
                <input type="file" accept="image/*" onChange={onRefFile} data-testid="look-ref-upload" className="lookportal__filein" />
              </label>
              {paletteRef && (
                <canvas ref={refCanvasRef} className="lookportal__refthumb" data-testid="look-ref-thumb" />
              )}
              <div className="lookportal__row lookportal__derive-go">
                <label className="lookportal__field lookportal__count">
                  Colours
                  <input
                    type="number"
                    min={2}
                    max={MAX_COLORS_PER_PALETTE}
                    value={deriveCount}
                    data-testid="look-derive-count"
                    onChange={(e) => setDeriveCount(e.target.value)}
                    onBlur={(e) => setDeriveCount(String(Math.max(2, Math.min(parseInt(e.target.value, 10) || 2, MAX_COLORS_PER_PALETTE))))}
                  />
                </label>
                <button type="button" className="lookportal__btn" data-testid="look-from-image" onClick={onDeriveFromImage}>
                  🎨 Derive palette
                </button>
              </div>
              {deriveMsg && (
                <p className="lookportal__hint" role="status" data-testid="look-derive-msg">{deriveMsg}</p>
              )}
            </details>
            <span className="lookportal__sublabel lookportal__colors-label">Colours ({look.colors.length})</span>
            <div className="lookportal__colors">
              {look.colors.map((c, i) => (
                <div
                  key={colorIds[i] ?? i}
                  className="lookportal__swatch"
                  draggable
                  title="Drag to reorder"
                  onDragStart={(e) => { dragIndex.current = i; e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); reorderColor(dragIndex.current, i); dragIndex.current = -1; }}
                >
                  {/* A button face + the in-house ColorPicker — Android's native color input
                      is an eight-preset dialog with "Custom" buried a tap deeper. */}
                  <button
                    type="button"
                    className="lookportal__swatchface"
                    style={{ background: c }}
                    aria-label={`color ${i + 1}: ${c}`}
                    title="Edit this colour"
                    data-testid={`swatch-${i}`}
                    onClick={() => setPickerIdx(i)}
                  />
                  <button
                    type="button"
                    className="lookportal__swatch-del"
                    aria-label={`remove color ${i + 1}`}
                    title="Remove this colour"
                    onClick={() => removeColorAt(i)}
                    disabled={look.colors.length <= 1}
                  >
                    ×
                  </button>
                  {/* Tap-to-move fallback: HTML5 drag never fires on touch, which made Gradient
                      mode (whose ramp IS the swatch order) unauthorable on phones/tablets. */}
                  <button
                    type="button"
                    className="lookportal__swatch-move lookportal__swatch-move--l"
                    aria-label={`move color ${i + 1} earlier`}
                    title="Move earlier"
                    onClick={() => reorderColor(i, i - 1)}
                    disabled={i === 0}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="lookportal__swatch-move lookportal__swatch-move--r"
                    aria-label={`move color ${i + 1} later`}
                    title="Move later"
                    onClick={() => reorderColor(i, i + 1)}
                    disabled={i === look.colors.length - 1}
                  >
                    ›
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="lookportal__swatch-add"
                aria-label="add color"
                title="Add a colour"
                onClick={addColor}
                disabled={look.colors.length >= MAX_COLORS_PER_PALETTE}
              >
                +
              </button>
            </div>
            {pickerIdx != null && look.colors[pickerIdx] != null && (
              <ColorPicker
                value={look.colors[pickerIdx]!}
                label={`Colour ${pickerIdx + 1}`}
                onChange={(hex) => setColors((cs) => cs.map((cc, j) => (j === pickerIdx ? hex : cc)))}
                onClose={() => setPickerIdx(null)}
              />
            )}
            <div className="lookportal__field">
              <span>Palette mode</span>
              <div className="lookportal__modes" role="group" aria-label="Palette mode">
                <button
                  type="button"
                  className={`lookportal__chip${look.mapMode === "luminance" ? " is-active" : ""}`}
                  aria-pressed={look.mapMode === "luminance"}
                  data-testid="look-mode-ramp"
                  onClick={() => setMapMode("luminance")}
                >
                  Brightness ramp
                </button>
                <button
                  type="button"
                  className={`lookportal__chip${look.mapMode === "nearest" ? " is-active" : ""}`}
                  aria-pressed={look.mapMode === "nearest"}
                  data-testid="look-mode-nearest"
                  onClick={() => setMapMode("nearest")}
                >
                  Nearest color
                </button>
                <button
                  type="button"
                  className={`lookportal__chip${look.mapMode === "gradient" ? " is-active" : ""}`}
                  aria-pressed={look.mapMode === "gradient"}
                  data-testid="look-mode-gradient"
                  title="Map brightness through the colours in the order you arranged them (drag swatches to reorder) — for rainbow/thermal ramps"
                  onClick={() => setMapMode("gradient")}
                >
                  Gradient
                </button>
              </div>
            </div>
            <details className="lookportal__help">
              <summary>How palettes work</summary>
              <p>The <b>palette</b> is the set of colors your photo is reduced to.</p>
              <ul>
                <li>
                  <b>More colors</b> → smoother gradients and finer detail. <b>Fewer</b> → bolder
                  and more abstract; the dither pattern blends the gaps between the colors you do have.
                </li>
                <li>
                  <b>Swatch order</b> matters only in <b>Gradient</b> mode, where it's the cold→hot
                  ramp brightness walks through (drag swatches to reorder). In <b>Brightness ramp</b>
                  the camera sorts the colors by brightness itself, and in <b>Nearest color</b> every
                  pixel just picks its closest color — so there, order is only for you to read.
                </li>
                <li>
                  <b>Brightness ramp</b> → the palette is a tonal ramp: each pixel is recolored by how
                  light or dark it is, <i>ignoring its hue</i> (a red and a green of the same brightness
                  become the same color). Best for mono / duotone / gradient looks.
                </li>
                <li>
                  <b>Nearest color</b> → each pixel snaps to its closest palette color by RGB, so the
                  original hues are kept (a red stays red-ish, a blue stays blue-ish).
                </li>
              </ul>
            </details>
          </fieldset>

          <fieldset className="lookportal__group">
            <legend>Adjust</legend>
            {adjustKeys.map((k) => (
              <div key={k} className="lookportal__slider">
                <span className="lookportal__slider-label">{k}</span>
                <input
                  type="range"
                  min={-100}
                  max={100}
                  value={look[k]}
                  aria-label={k}
                  list="lookportal-adjust-ticks"
                  onChange={(e) => patch({ [k]: Number(e.target.value) } as Partial<EditableLook>)}
                  onDoubleClick={() => patch({ [k]: 0 } as Partial<EditableLook>)}
                />
                <input
                  type="number"
                  min={-100}
                  max={100}
                  value={adjustDraft[k] ?? look[k]}
                  aria-label={`${k} value`}
                  className="lookportal__slider-num"
                  data-testid={`look-adjust-${k}`}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setAdjustDraft((d) => ({ ...d, [k]: raw }));
                    // "" or a lone "-" is a partial edit — keep showing it, commit nothing yet.
                    if (raw !== "" && raw !== "-")
                      patch({ [k]: Math.max(-100, Math.min(100, parseInt(raw, 10) || 0)) } as Partial<EditableLook>);
                  }}
                  onBlur={() => setAdjustDraft((d) => { const next = { ...d }; delete next[k]; return next; })}
                />
                <button
                  type="button"
                  className="lookportal__slider-zero"
                  aria-label={`reset ${k}`}
                  title={`Reset ${k} to 0`}
                  onClick={() => patch({ [k]: 0 } as Partial<EditableLook>)}
                  disabled={look[k] === 0}
                >
                  ↺
                </button>
              </div>
            ))}
            {/* a single tick at 0 (the neutral point) on every Adjust slider */}
            <datalist id="lookportal-adjust-ticks"><option value="0" /></datalist>
            <button
              type="button"
              className="lookportal__reset"
              onClick={() => patch({ brightness: 0, contrast: 0, saturation: 0 })}
              disabled={look.brightness === 0 && look.contrast === 0 && look.saturation === 0}
            >
              Reset all
            </button>
          </fieldset>

          {/* Tone ops — committed to the Look + synced to the camera (graduated, #164 P4). gamma is
              PERCENT (100 = identity); they drive `look` via patch(...), so they show in the preview
              AND commit. */}
          <fieldset className="lookportal__group" data-testid="look-tone">
            <legend>Tone</legend>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">gamma</span>
              <input
                type="range" min={50} max={200} step={5} value={look.gamma} aria-label="gamma"
                onChange={(e) => patch({ gamma: Number(e.target.value) })}
                onDoubleClick={() => patch({ gamma: 100 })}
              />
              <span className="lookportal__slider-num">{look.gamma}%</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset gamma" title="Reset gamma" onClick={() => patch({ gamma: 100 })} disabled={look.gamma === 100}>↺</button>
            </div>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">hue</span>
              <input
                type="range" min={-180} max={180} step={5} value={look.hueRotate} aria-label="hue"
                onChange={(e) => patch({ hueRotate: Number(e.target.value) })}
                onDoubleClick={() => patch({ hueRotate: 0 })}
              />
              <span className="lookportal__slider-num">{look.hueRotate}°</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset hue" title="Reset hue" onClick={() => patch({ hueRotate: 0 })} disabled={look.hueRotate === 0}>↺</button>
            </div>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">posterize</span>
              <input
                type="range" min={0} max={8} step={1} value={look.posterize} aria-label="posterize"
                onChange={(e) => patch({ posterize: Number(e.target.value) })}
                onDoubleClick={() => patch({ posterize: 0 })}
              />
              <span className="lookportal__slider-num">{look.posterize < 2 ? "off" : look.posterize}</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset posterize" title="Reset posterize" onClick={() => patch({ posterize: 0 })} disabled={look.posterize === 0}>↺</button>
            </div>
            <label className="lookportal__check">
              <input type="checkbox" checked={look.negative} onChange={(e) => patch({ negative: e.target.checked })} />
              Negative (invert)
            </label>
            <label className="lookportal__check">
              <input type="checkbox" checked={look.solarize} onChange={(e) => patch({ solarize: e.target.checked })} />
              Solarize (Sabattier)
            </label>
            <button
              type="button"
              className="lookportal__reset"
              onClick={() => patch({ gamma: 100, posterize: 0, hueRotate: 0, negative: false, solarize: false })}
              disabled={look.gamma === 100 && look.posterize === 0 && look.hueRotate === 0 && !look.negative && !look.solarize}
            >
              Reset tone
            </button>
            <p className="lookportal__stylehelp">
              Per-Look tone ops — committed with the Look and synced to the camera. Gamma = tone curve
              (100% = none); hue = hue rotation; posterize = N tone levels; Negative/Solarize invert tones.
            </p>
          </fieldset>

          {/* Selective keys (#235, ADR-0043) — Luma Key / Chroma Key / Splash. Committed with the
              Look and synced to the camera (graduated from the Phase-0 prototype; the starter
              Looks depend on them). */}
          <fieldset className="lookportal__group" data-testid="look-glow">
            <legend>Selective</legend>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">luma key</span>
              <input type="range" min={0} max={255} step={1} value={look.lumaKey ?? 0} aria-label="luma key"
                onChange={(e) => patch({ lumaKey: Number(e.target.value) })} onDoubleClick={() => patch({ lumaKey: 0 })} />
              <span className="lookportal__slider-num">{(look.lumaKey ?? 0) === 0 ? "off" : look.lumaKey}</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset luma key" title="Reset luma key" onClick={() => patch({ lumaKey: 0 })} disabled={(look.lumaKey ?? 0) === 0}>↺</button>
            </div>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">chroma key</span>
              <input type="range" min={0} max={255} step={1} value={look.chromaKey ?? 0} aria-label="chroma key"
                onChange={(e) => patch({ chromaKey: Number(e.target.value) })} onDoubleClick={() => patch({ chromaKey: 0 })} />
              <span className="lookportal__slider-num">{(look.chromaKey ?? 0) === 0 ? "off" : look.chromaKey}</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset chroma key" title="Reset chroma key" onClick={() => patch({ chromaKey: 0 })} disabled={(look.chromaKey ?? 0) === 0}>↺</button>
            </div>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">splash</span>
              <input type="range" min={0} max={100} step={1} value={look.splash ?? 0} aria-label="splash"
                onChange={(e) => patch({ splash: Number(e.target.value) })} onDoubleClick={() => patch({ splash: 0 })} />
              <span className="lookportal__slider-num">{(look.splash ?? 0) === 0 ? "off" : look.splash}</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset splash" title="Reset splash" onClick={() => patch({ splash: 0 })} disabled={(look.splash ?? 0) === 0}>↺</button>
            </div>
            <p className="lookportal__stylehelp">
              Selective keys grey out part of the frame so a subject stands out, and all three read best with
              <b> nearest</b> mapping — <b>Splash</b> needs it, and the other two turn subtle without it.
              <b>Luma key</b> (0–255) greys pixels <em>darker</em> than the threshold, sinking the
              shadows to the palette&apos;s greys. <b>Chroma key</b> (0–255) greys <em>washed-out</em>,
              low-saturation pixels (haze, pale sky) so a vividly-coloured subject pops — higher = more of the
              background falls away. <b>Splash</b> (0–100) greys pixels whose colour is <em>far from every
              palette colour</em>, keeping only things that match your palette&apos;s hues (a colour-splash);
              lower = stricter.
            </p>
          </fieldset>

          {/* Vignette (#238) — the first spatial Look field. Sliders drive `look` via patch(...), so they
              preview AND commit. Strength is signed: + darkens the rim (spotlight), - brightens (halo). */}
          <fieldset className="lookportal__group" data-testid="look-vignette">
            <legend>Vignette</legend>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">strength</span>
              <input type="range" min={-100} max={100} step={1} value={look.vignette ?? 0} aria-label="vignette strength"
                onChange={(e) => patch({ vignette: Number(e.target.value) })} onDoubleClick={() => patch({ vignette: 0 })} />
              <span className="lookportal__slider-num">{(look.vignette ?? 0) === 0 ? "off" : look.vignette}</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset vignette strength" title="Reset strength" onClick={() => patch({ vignette: 0 })} disabled={(look.vignette ?? 0) === 0}>↺</button>
            </div>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">radius</span>
              <input type="range" min={0} max={100} step={1} value={look.vignetteRadius ?? 0} aria-label="vignette radius"
                onChange={(e) => patch({ vignetteRadius: Number(e.target.value) })} onDoubleClick={() => patch({ vignetteRadius: 0 })} />
              <span className="lookportal__slider-num">{look.vignetteRadius ?? 0}</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset vignette radius" title="Reset radius" onClick={() => patch({ vignetteRadius: 0 })} disabled={(look.vignetteRadius ?? 0) === 0}>↺</button>
            </div>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">feather</span>
              <input type="range" min={0} max={100} step={1} value={look.vignetteFeather ?? 0} aria-label="vignette feather"
                onChange={(e) => patch({ vignetteFeather: Number(e.target.value) })} onDoubleClick={() => patch({ vignetteFeather: 0 })} />
              <span className="lookportal__slider-num">{look.vignetteFeather ?? 0}</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset vignette feather" title="Reset feather" onClick={() => patch({ vignetteFeather: 0 })} disabled={(look.vignetteFeather ?? 0) === 0}>↺</button>
            </div>
            <p className="lookportal__stylehelp">
              A radial edge-burn native to the round screen. <b>Strength</b> (−100…100) darkens the rim into a
              spotlight (+) or brightens it into a halo (−); 0 = off. <b>Radius</b> (0–100) grows the untouched
              centre. <b>Feather</b> (0–100) softens the clean→burn transition.
            </p>
          </fieldset>

          {/* Dither & ASCII — committed to the Look + synced to the camera (graduated, #164 P5). The
              strength/bias knobs drive `look` via patch(...), so they preview AND commit.
              ditherStrength/glyphSize are PERCENT (100 = native). The glyph set/size rows are ASCII-only. */}
          <fieldset className="lookportal__group" data-testid="look-dither-knobs">
            <legend>Dither &amp; ASCII</legend>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">strength</span>
              <input
                type="range" min={0} max={200} step={5} value={look.ditherStrength} aria-label="dither strength"
                onChange={(e) => patch({ ditherStrength: Number(e.target.value) })}
                onDoubleClick={() => patch({ ditherStrength: 100 })}
              />
              <span className="lookportal__slider-num">{look.ditherStrength}%</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset dither strength" title="Reset dither strength" onClick={() => patch({ ditherStrength: 100 })} disabled={look.ditherStrength === 100}>↺</button>
            </div>
            <div className="lookportal__slider">
              <span className="lookportal__slider-label">bias</span>
              <input
                type="range" min={-64} max={64} step={4} value={look.ditherBias} aria-label="dither bias"
                onChange={(e) => patch({ ditherBias: Number(e.target.value) })}
                onDoubleClick={() => patch({ ditherBias: 0 })}
              />
              <span className="lookportal__slider-num">{look.ditherBias}</span>
              <button type="button" className="lookportal__slider-zero" aria-label="reset dither bias" title="Reset dither bias" onClick={() => patch({ ditherBias: 0 })} disabled={look.ditherBias === 0}>↺</button>
            </div>
            {isAscii && (
              <>
                <label className="lookportal__field">
                  Glyph set
                  <select
                    aria-label="glyph set"
                    data-testid="look-glyphset"
                    value={look.glyphSet}
                    onChange={(e) => patch({ glyphSet: e.target.value as EditableLook["glyphSet"] })}
                  >
                    <option value="dots">Dots (default)</option>
                    <option value="blocks">Blocks (level fill)</option>
                    <option value="lines">Lines (scanlines)</option>
                    <option value="characters">Characters · 10 (8×8 type)</option>
                    <option value="characters16">Characters · 16 (8×8 type)</option>
                  </select>
                </label>
                <div className="lookportal__slider">
                  <span className="lookportal__slider-label">glyph size</span>
                  <input
                    type="range" min={50} max={300} step={5} value={look.glyphSize} aria-label="glyph size"
                    onChange={(e) => patch({ glyphSize: Number(e.target.value) })}
                    onDoubleClick={() => patch({ glyphSize: 100 })}
                  />
                  <span className="lookportal__slider-num">{look.glyphSize}% · {asciiCellPx}px</span>
                  <button type="button" className="lookportal__slider-zero" aria-label="reset glyph size" title="Reset glyph size" onClick={() => patch({ glyphSize: 100 })} disabled={look.glyphSize === 100}>↺</button>
                </div>
              </>
            )}
            <button
              type="button"
              className="lookportal__reset"
              onClick={() => patch({ ditherStrength: 100, ditherBias: 0, glyphSet: "dots", glyphSize: 100 })}
              disabled={look.ditherStrength === 100 && look.ditherBias === 0 && look.glyphSet === "dots" && look.glyphSize === 100}
            >
              Reset dither
            </button>
            <p className="lookportal__stylehelp">
              Per-Look dither knobs — committed with the Look and synced to the camera. Strength scales the
              dither amplitude and bias leans it lighter/darker (ordered + Floyd/Atkinson/Sierra). For ASCII,
              choose a glyph set and its size.
            </p>
          </fieldset>
        </div>
      </div>

      {zoom && createPortal(
        <div
          className="sheet-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged preview"
          data-testid="look-zoom"
          onClick={() => setZoom(null)}
        >
          <div className="sheet-overlay__panel" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="sheet-overlay__close" onClick={() => setZoom(null)} aria-label="Close">
              ×
            </button>
            {/* Live mirror of each pane (see the effect above): raw Source beside the chosen preview
                for A/B. Square box + border-radius gives a true circle like the on-page previews. */}
            <div className="lookportal__zoomrow">
              {zoom.panes.map((pane, i) => {
                const size = zoom.panes.length > 1 ? "min(40vmin, 42vw, 440px)" : "min(80vmin, 600px)";
                return (
                  <figure key={pane.caption} className="lookportal__zoompane">
                    <canvas
                      ref={(el) => { zoomCanvasRefs.current[i] = el; }}
                      aria-label={`${pane.caption}, enlarged`}
                      style={{ imageRendering: "pixelated", width: size, height: size, background: "#000", borderRadius: "50%" }}
                    />
                    <figcaption className="lookportal__zoomcap">{pane.caption}</figcaption>
                  </figure>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
}
